import { computed, ref } from "vue";
import { defineStore } from "pinia";
import type {
  IRegisterParams,
  IFacSocialLoginParams,
  ILoginParams,
  IResetPasswordParams,
} from "~/types/account";
import {
  register,
  login,
  smsLogin,
  resetPassword,
  facSocialLogin,
  logout,
  refreshToken,
} from "~/apis/account";
import { Cache } from "~/types/cache";
import { deepClone } from "~/utils/is";
import { navigateTo, useCookie } from "nuxt/app";
import { useFirstLoginStore } from "~/store/firstLogin";
import { NUXT_TEXTING } from "~/types/env";

export const useAccountStore = defineStore(
  Cache.ACCOUNT_INFO,
  () => {
    const { $i18n } = useNuxtApp();
    const { t } = $i18n;
    const uuid = ref();
    const user_id = ref();
    const access_token = ref();
    const refresh_token = ref();
    const expires_time = ref();
    const openid = ref();
    const avatar = ref();
    const email = ref();
    const nickname = ref();
    const userSetting = ref();
    const facToken = useCookie(Cache.FAC_TOKEN);
    // 个人信息
    // 个人视角的社媒链接
    const socialLinks = ref<string[]>([]);
    /** Set data */
    const setData = (data?: any) => {
      if (!!data && !!deepClone(data)) {
        const copyData = deepClone(data);
        uuid.value = copyData.uuid || uuid.value;
        user_id.value = copyData.userId || user_id.value;
        access_token.value = copyData.accessToken || access_token.value;
        refresh_token.value = copyData.refreshToken || refresh_token.value;
        expires_time.value = copyData.expiresTime || expires_time.value;
        openid.value = copyData.openid || openid.value;
        avatar.value = copyData.avatar || avatar.value;
        email.value = copyData.email || email.value;
        nickname.value = copyData.nickname || nickname.value;
        userSetting.value = copyData.userSetting || userSetting.value;
      } else {
        uuid.value = null;
        user_id.value = null;
        access_token.value = null;
        refresh_token.value = null;
        expires_time.value = null;
        openid.value = null;
        facToken.value = null;
        avatar.value = null;
        email.value = null;
        nickname.value = null;
        userSetting.value = null;
      }
    };
    const setNickNameData = (val: string) => {
      nickname.value = val;
    };
    const updateSocialLinks = (links: string[]) => {
      socialLinks.value = links;
    };
    /** 是否已登录 */
    const isLogin = computed(() => !!access_token.value);
    /** 登录 */
    const doLogin = async (params: ILoginParams) => {
      return new Promise((resolve, reject) => {
        login(params)
          .then((res: any) => {
            console.log("登录 res:", res);
            if (res.code === 0) {
              setData(res.data);
              resolve(true);
              loginSuccess(res.data);
            } else {
              reject(res.code);
            }
          })
          .catch((err) => {
            console.log("登录失败 err:", err);
            reject(err);
          });
      });
    };
    /** 短信登录 */
    const doSMSLogin = async (params: ILoginParams) => {
      return new Promise((resolve, reject) => {
        smsLogin(params)
          .then((res: any) => {
            console.log("短信登录 res:", res);
            if (res.code === 0) {
              setData(res.data);
              resolve(true);
              loginSuccess(res.data);
            } else {
              reject(res.code);
            }
          })
          .catch((err) => {
            console.log("短信登录失败 err:", err);
            reject(err);
          });
      });
    };
    /** 第三方社媒登录 */
    const doFacSocialLogin = async (params: IFacSocialLoginParams) => {
      return new Promise((resolve, reject) => {
        facSocialLogin(params)
          .then((res: any) => {
            console.log("第三方社媒登录 res:", res);
            if (res.code === 0) {
              setData(res.data);
              resolve(true);
              loginSuccess(res.data);
            } else {
              reject(res.code);
            }
          })
          .catch((error) => {
            reject(error);
          });
      });
    };
    /** 注册 */
    const doRegister = async (params: IRegisterParams) => {
      return new Promise((resolve, reject) => {
        register(params)
          .then((res: any) => {
            console.log("注册 res:", res);
            if (res.code === 0) {
              setData(res.data);
              resolve(true);
              loginSuccess(res.data);
            } else {
              reject(res.code);
            }
          })
          .catch((err) => {
            console.log("注册失败 err:", err);
            reject(err);
          });
      });
    };
    /** 修改密码 */
    const doResetPassword = async (params: IResetPasswordParams) => {
      return new Promise((resolve, reject) => {
        resetPassword(params)
          .then((res: any) => {
            console.log("修改密码 res:", res);
            if (res.code === 0) {
              resolve(true);
            } else {
              reject(res.code);
            }
          })
          .catch((err) => {
            console.log("修改密码失败 err:", err);
            reject(err);
          });
      });
    };
    /** 登出 */
    const doLogout = () => {
      logout();
      setData();
      facToken.value = null;
      doClearCache();
      // 判断是否开启白名单
      if (NUXT_TEXTING) {
        backToLogin();
      } else {
        backToHome();
      }
    };
    /** token失效后的清除账号信息 */
    const doClearAccount = () => {
      setData();
      facToken.value = null;
      doClearCache();
    };
    /** 登录成功后路由跳转 */
    const loginSuccess = async (data: any) => {
      const firstLoginStore = useFirstLoginStore();
      useToast.success(t("toast.login_success"));
      const route = useRoute();
      if (!!route.query.to) {
        await navigateTo(route.query.to);
      } else {
        await navigateTo("/");
      }
      firstLoginStore.setFirstLogin(!data.hasInitSetting);
    };
    const toLoginWithPath = (path: string) => {
      return navigateTo({
        path: "/account/login",
        query: {
          to: path,
        },
      });
    };

    const toLoginReplaceWithPath = (path: string) => {
      const router = useRouter();
      router.replace(`/account/login?to=${path}`);
    };

    const toLoginCheckPath = () => {
      const route = useRoute();
      return toLoginWithPath(route.path);
    };

    /** 重置 Token */
    const doRefreshToken = () => {
      const postData = {
        refreshToken: refresh_token.value,
      };
      setTimeout(() => {
        refreshToken(postData)
          .then((res: any) => {
            console.log("重置 Token res:", res);
            res.code === 0 ? setData(res.data) : doLogout();
          })
          .catch((err) => {
            console.log("重置 Token 失败 err:", err);
            doLogout();
          });
      });
    };
    /** 清除本地缓存 */
    const doClearCache = () => {
      localStorage.removeItem(Cache.UPLOAD_MODEL_DRAFT);
      // 退出登录时不清除设备及热盘缓存
      // deviceCache.clear();
      // plateCache.clear();
    };

    return {
      uuid,
      user_id,
      access_token,
      refresh_token,
      expires_time,
      openid,
      avatar,
      email,
      nickname,
      userSetting,
      socialLinks,
      isLogin,
      updateSocialLinks,
      setData,
      doLogin,
      doSMSLogin,
      doRegister,
      doResetPassword,
      doFacSocialLogin,
      doLogout,
      doRefreshToken,
      toLoginWithPath,
      toLoginCheckPath,
      doClearAccount,
      setNickNameData,
      toLoginReplaceWithPath,
    };
  },
  {
    persist: {
      storage: persistedState.localStorage,
    },
  }
);
